import React, { useEffect } from "react";
import * as c from "./pricingComparison.module.scss";
import classNames from "classnames";
import { graphql, Link, useStaticQuery } from "gatsby";
import IconCheckmarkBolder from "@components/icons/iconCheckmarkBolder";
import { Text } from "@components/shared";
import { createItemSortOrder } from "@utils/helpers";
import chat from "@utils/chat";

const featureOrder = {
  scheduling: 0,
  "client-management": 1,
  "online-booking": 2,
  "two-way-texting": 3,
  "retail-and-inventory": 4,
  "staff-management": 5,
  "gift-cards": 6,
  "mobile-apps": 7,
  reporting: 8,
  "multi-location": 9,
};

const getFeatureUrl = (slug) => {
  if (slug === "payments") {
    return "/payments/";
  }
  return `/features/${slug}/`;
};

const getFeatureSortOrder = createItemSortOrder(featureOrder);

const FirstCol = ({ label, link, sublabel, labelMobile, labelClassName }) => {
  return (
    <th className={c.firstCol}>
      <div className={c.firstColInner}>
        {label && (
          <div className={classNames(c.firstColLabel, labelClassName)}>
            {link ? (
              <Link to={link}>{label}</Link>
            ) : (
              <>
                <Text variant="p4" className={c.firstColLabelDesktop}>
                  {label}
                </Text>
                <Text variant="p4" className={c.firstColLabelMobile}>
                  {labelMobile || label}
                </Text>
              </>
            )}
          </div>
        )}
        {sublabel && (
          <Text variant="p5" className={c.firstColSublabel}>
            {sublabel}
          </Text>
        )}
      </div>
    </th>
  );
};

const PlanHeaderCol = ({ title, who }) => {
  return (
    <th className={classNames(c.planCol, c.planHeaderCol)}>
      <div className={c.planHeaderColWrapper}>
        <div className={c.planHeaderColInner}>
          <Text variant="h6">{title}</Text>
          <Text variant="p4" className={c.planWho}>
            {who}
          </Text>
        </div>
      </div>
    </th>
  );
};

const PriceCol = ({ price }) => {
  return (
    <td className={c.planCol}>
      <div className={c.priceColInner}>
        <Text variant="h3">${price}</Text>
        <Text variant="h6">${price}</Text>
        <Text variant="p4" className={c.pricePeriod}>
          /mo
        </Text>
      </div>
    </td>
  );
};

const SectionHeader = ({ title, link, singleColumn }) => {
  return (
    <tr className={c.sectionHeaderRow}>
      <th colSpan={1}>{link ? <Link to={link}>{title}</Link> : <Text variant="p4-bold">{title}</Text>}</th>
      {singleColumn ? (
        <th colSpan={3} />
      ) : (
        <>
          <th colSpan={1} />
          <th colSpan={1} />
          <th colSpan={1} />
        </>
      )}
    </tr>
  );
};

const FeatureCol = ({
  className,
  check,
  label,
  labelMobile,
  extraInfo,
  extraInfoMobile,
  fullSpan,
  rowSpan = 1,
  sublabels = [],
  sublabelsMobile = [],
}) => {
  return (
    <td
      className={classNames(className, c.planCol, { [c.planColFullWidth]: fullSpan })}
      colSpan={fullSpan ? 3 : null}
      rowSpan={rowSpan}
    >
      <div className={c.featureColInner}>
        {check ? (
          <IconCheckmarkBolder className={c.iconCheckmark} />
        ) : (
          <>
            <Text variant="p4" className={c.planColLabel}>
              {label}
            </Text>
            <Text variant="p4" className={c.planColLabelMobile}>
              {labelMobile || label}
            </Text>
            {extraInfo && (
              <Text variant="p4" className={c.planColExtraInfo}>
                {extraInfo}
              </Text>
            )}
            {extraInfoMobile && (
              <Text variant="p4" className={c.planColExtraInfoMobile}>
                {extraInfoMobile}
              </Text>
            )}
            {sublabels.map((sublabel) => (
              <Text key={sublabel} variant="p5" className={c.featureColSublabel}>
                {sublabel}
              </Text>
            ))}
            {sublabelsMobile.map((sublabel) => (
              <Text key={sublabel} variant="p5" className={c.featureColSublabelMobile}>
                {sublabel}
              </Text>
            ))}
          </>
        )}
      </div>
    </td>
  );
};

const FeatureRow = ({ feature, plan1 = true, plan2 = true, plan3 = true, className }) => {
  return (
    <tr className={className}>
      <FirstCol label={feature.title} link={feature.fullSlug ? `${feature.slug}` : getFeatureUrl(feature.slug)} />
      <FeatureCol check={plan1} label={plan1 ? null : "-"} />
      <FeatureCol check={plan2} label={plan2 ? null : "-"} />
      <FeatureCol check={plan3} label={plan3 ? null : "-"} />
    </tr>
  );
};

export default function PricingComparison() {
  const headerRef = React.useRef(null);

  const { featurePages } = useStaticQuery(
    graphql`
      query {
        featurePages: allContentfulLandingPage(
          filter: {
            slug: {
              nin: [
                "why-mangomint"
                "why-mangomint-alt"
                "marketing-and-automation"
                "campaigns"
                "automated-flows"
                "memberships-and-packages"
                "express-booking"
                "virtual-waiting-room"
                "integrated-forms"
                "two-way-texting"
                "payroll-processing"
                "email-marketing"
              ]
            }
            pageVariant: { in: ["Feature Page"] }
          }
        ) {
          nodes {
            title: navbarOverviewTitle
            slug
          }
        }
      }
    `
  );

  const sortedFeatures = featurePages?.nodes?.sort((a, b) => getFeatureSortOrder(a.slug) - getFeatureSortOrder(b.slug));

  let featureRows =
    sortedFeatures?.map((feature, idx) => {
      return <FeatureRow feature={feature} key={idx} />;
    }) || [];

  featureRows.push(
    <FeatureRow
      feature={{
        title: "Packages",
        slug: "memberships-and-packages",
      }}
      key="packages"
    />
  );

  featureRows.push(
    <FeatureRow
      plan1={false}
      feature={{
        title: "Memberships",
        slug: "memberships-and-packages",
      }}
      key="memberships"
    />
  );

  featureRows.push(
    <FeatureRow
      plan1={false}
      feature={{
        title: "Waiting Room",
        slug: "virtual-waiting-room",
      }}
      key="waiting-room"
    />
  );

  featureRows.push(
    <FeatureRow
      plan1={false}
      feature={{
        title: "Express Booking™",
        slug: "express-booking",
      }}
      key="express-booking"
    />
  );

  featureRows.push(
    <FeatureRow
      plan1={false}
      feature={{
        title: "HIPAA Compliance",
        slug: "/learn/hipaa-compliance/",
        fullSlug: true,
      }}
      key="hipaa"
      className={c.lastSectionTr} // This class needs to be put on the last <tr> before a section header
    />
  );

  const onScroll = () => {
    if (!headerRef.current) return;

    const headerTop = headerRef.current.getBoundingClientRect().top;
    const stuckAttribute = headerRef.current.getAttribute("data-stuck");

    if (headerTop <= 80) {
      if (!stuckAttribute || stuckAttribute === "false") {
        headerRef.current.setAttribute("data-stuck", "true");
      }
    } else {
      if (stuckAttribute === "true") {
        headerRef.current.setAttribute("data-stuck", "false");
      }
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div className={c.top}>
        <Text variant="h2">Compare plans</Text>
        <div className={c.subline}>
          <Text variant="p3"> Want to talk to a human about our plans? </Text>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              chat.open();
            }}
          >
            Start a chat
          </a>
        </div>
      </div>

      <div className={c.tableCtn}>
        <div className={c.tableCtnInner}>
          <table className={c.table}>
            <thead>
              <tr className={c.headerRow} ref={headerRef}>
                <FirstCol />
                <PlanHeaderCol title="Essentials" who="Small businesses" />
                <PlanHeaderCol title="Standard" who="Medium businesses" />
                <PlanHeaderCol title="Unlimited" who="Large businesses" />
              </tr>
            </thead>
            <tbody className={c.bodyTable}>
              <tr>
                <FirstCol label="Monthly Price (USD)" labelClassName={c.monthlyPriceLabel} />
                <PriceCol price="165" />
                <PriceCol price="245" />
                <PriceCol price="375" />
              </tr>
              <tr>
                <FirstCol label="Service Providers" sublabel="Only staff that provide services are counted" />
                <FeatureCol label="2 to 10" labelMobile="10" />
                <FeatureCol label="Up to 20" labelMobile="20" />
                <FeatureCol label="Unlimited" labelMobile="∞" />
              </tr>
              <tr className={c.lastSectionTr}>
                <FirstCol label="Additional Locations" sublabel="First location included in each plan" />
                <FeatureCol label="$95" extraInfo="/month" />
                <FeatureCol label="$135" extraInfo="/month" />
                <FeatureCol label="$175" extraInfo="/month" />
              </tr>

              <SectionHeader title="Features" />
              {featureRows}

              <SectionHeader title="Marketing & Automation" singleColumn={true} />

              <tr>
                <FirstCol label="Email Campaigns" link="/features/campaigns/" />
                <FeatureCol
                  className={c.leftBoxShadow}
                  label="Pay as you go (1¢ per marketing credit)"
                  sublabels={[
                    "Save with credit subscriptions starting at $50/month for 6,000 credits.",
                    "Discounts up to 67% available for high credit volumes.",
                  ]}
                  labelMobile="Pay as you go"
                  sublabelsMobile={["1¢ per marketing credit.", "Volume discounts available."]}
                  fullSpan={true}
                  rowSpan={2}
                />
              </tr>
              <tr className={c.lastSectionTr}>
                <FirstCol label="Automated Flows" link="/features/automated-flows/" />
              </tr>
              <SectionHeader title="Payment Processing" singleColumn={true} />

              <tr>
                <FirstCol label="In-person transactions" />
                <FeatureCol label="2.45% + 15¢" fullSpan={true} />
              </tr>
              <tr>
                <FirstCol label="Virtual transactions" />
                <FeatureCol label="2.90% + 20¢" fullSpan={true} />
              </tr>
              <tr>
                <FirstCol label="Custom rates" />
                <FeatureCol
                  label="Businesses with over $50k/mo in volume may qualify for custom rates"
                  labelMobile="Some businesses qualify"
                  fullSpan={true}
                />
              </tr>
              <tr className={c.lastSectionTr}>
                <FirstCol label="Bluetooth card-reader" />
                <FeatureCol label="$100" extraInfo="/device" />
                <FeatureCol label="1 included" />
                <FeatureCol label="1 included" />
              </tr>

              <SectionHeader title="Integrations" link="/integrations/" />
              <tr className={c.lastSectionTr}>
                <FirstCol label="Shopify" />
                <FeatureCol label="-" />
                <FeatureCol label="$50" extraInfo="/month" />
                <FeatureCol check={true} />
              </tr>

              <SectionHeader title="Setup & Onboarding" />

              <tr>
                <FirstCol
                  label="One-on-one personalized onboarding"
                  sublabel="Dedicated onboarding manager"
                  labelMobile="1-on-1 personalized onboarding"
                />
                <FeatureCol check={true} />
                <FeatureCol check={true} />
                <FeatureCol check={true} />
              </tr>
              <tr>
                <FirstCol label="Standard data import" sublabel="Client data, products, inventory, gift cards, etc" />
                <FeatureCol check={true} />
                <FeatureCol check={true} />
                <FeatureCol check={true} />
              </tr>
              <tr className={c.lastSectionTr}>
                <FirstCol label="Advanced data import" sublabel="More complicated data that requires manual work" />
                <FeatureCol label="-" />
                <FeatureCol label="Upon request" />
                <FeatureCol label="Upon request" />
              </tr>

              <SectionHeader title="Customer support" />

              <tr>
                <FirstCol label="In-app chat support" sublabel="Fast, effective, and friendly support" />
                <FeatureCol check={true} />
                <FeatureCol check={true} />
                <FeatureCol check={true} />
              </tr>
              <tr>
                <FirstCol label="On-demand phone support" sublabel="Free scheduled calls and check-ins" />
                <FeatureCol check={true} />
                <FeatureCol check={true} />
                <FeatureCol check={true} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
